import React, { useEffect, useState } from 'react';
import { Card, FormField, Input, Select, Text } from '@heartinternet/hi-ui';
import type { OrgFormData } from '../types';
import { ASCII_REGEX, EMAIL_REGEX, PHONE_REGEX, validateOrgField } from '../helpers';
import { COUNTRY_LIST } from './countries';

/** ideal-postcodes global injected by the CDN script (see ssl-info.tt). */
interface IdealPostcodesGlobal {
  AddressFinder: {
    watch: (config: Record<string, unknown>) => unknown;
  };
}
declare global {
  interface Window {
    IdealPostcodes?: IdealPostcodesGlobal;
  }
}

type OrgFieldKey =
  | 'organisation'
  | 'jobTitle'
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'address1'
  | 'address2'
  | 'city'
  | 'state'
  | 'postcode';

interface FieldSpec {
  key: OrgFieldKey;
  label: string;
  type: 'text' | 'email' | 'tel';
  required: boolean;
  regex: RegExp;
  helperText?: string;
  /** Address fields are disabled until a country is chosen. */
  addressField?: boolean;
  isPhone?: boolean;
}

const FIELD_SPECS: FieldSpec[] = [
  {
    key: 'organisation',
    label: 'Organisation name',
    type: 'text',
    required: true,
    regex: ASCII_REGEX,
  },
  { key: 'firstName', label: 'First name', type: 'text', required: true, regex: ASCII_REGEX },
  { key: 'lastName', label: 'Last name', type: 'text', required: true, regex: ASCII_REGEX },
  { key: 'email', label: 'Email', type: 'email', required: true, regex: EMAIL_REGEX },
  {
    key: 'phone',
    label: 'Phone',
    type: 'tel',
    required: true,
    regex: PHONE_REGEX,
    helperText: 'Intl. format, e.g. +44.1234567891',
    isPhone: true,
  },
  {
    key: 'address1',
    label: 'Address 1',
    type: 'text',
    required: true,
    regex: ASCII_REGEX,
    addressField: true,
  },
  {
    key: 'address2',
    label: 'Address 2',
    type: 'text',
    required: false,
    regex: ASCII_REGEX,
    addressField: true,
  },
  {
    key: 'city',
    label: 'City',
    type: 'text',
    required: true,
    regex: ASCII_REGEX,
    addressField: true,
  },
  {
    key: 'state',
    label: 'County / State / Province / Territory',
    type: 'text',
    required: false,
    regex: ASCII_REGEX,
    addressField: true,
  },
  {
    key: 'postcode',
    label: 'Postcode / Zip',
    type: 'text',
    required: true,
    regex: ASCII_REGEX,
    addressField: true,
  },
];

export interface OrgDetailsFormProps {
  isEV: boolean;
  value: OrgFormData;
  onChange: (patch: Partial<OrgFormData>) => void;
  onValidChange: (valid: boolean) => void;
  showErrors: boolean;
  invalid: boolean;
  idealPostcodesApiKey: string;
}

/** Address field ids the finder writes to. */
const ADDRESS_IDS: OrgFieldKey[] = ['address1', 'address2', 'city', 'state', 'postcode'];

/** Organisation Details card — mirrors submission/section-form.js. Only shown for OV/EV certs. */
export const OrgDetailsForm: React.FC<OrgDetailsFormProps> = ({
  isEV,
  value,
  onChange,
  onValidChange,
  showErrors,
  invalid,
  idealPostcodesApiKey,
}) => {
  const [touched, setTouched] = useState<Partial<Record<string, boolean>>>({});
  const countrySelected = Boolean(value.country);

  const specs = isEV
    ? [
        FIELD_SPECS[0],
        {
          key: 'jobTitle' as const,
          label: 'Job title',
          type: 'text' as const,
          required: false,
          regex: ASCII_REGEX,
        },
        ...FIELD_SPECS.slice(1),
      ]
    : FIELD_SPECS;

  const errorFor = (spec: FieldSpec): string | null =>
    validateOrgField(value[spec.key] ?? '', spec.required, spec.regex, Boolean(spec.isPhone));

  // Report validity whenever values change: all required fields valid + country selected.
  useEffect(() => {
    const allValid = specs.every((s) => errorFor(s) === null) && countrySelected;
    onValidChange(allValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isEV]);

  // Wire the ideal-postcodes address finder once a country is chosen — the address
  // section (including #ssl-ideal) is only mounted then. A no-op if the CDN global
  // is absent, e.g. in tests.
  useEffect(() => {
    if (!countrySelected) return;
    const ideal = window.IdealPostcodes;
    if (!ideal) return;
    ideal.AddressFinder.watch({
      apiKey: idealPostcodesApiKey,
      inputField: '#ssl-ideal',
      outputFields: {
        line_1: '#ssl-address1',
        line_2: '#ssl-address2',
        post_town: '#ssl-city',
        county: '#ssl-state',
        postcode: '#ssl-postcode',
      },
      onAddressRetrieved: () => {
        // Sync the finder's DOM writes back into React state.
        const patch: Partial<OrgFormData> = {};
        for (const id of ADDRESS_IDS) {
          const el = document.getElementById(`ssl-${id}`) as HTMLInputElement | null;
          /* v8 ignore next -- the address inputs are always mounted, so el is never null here */
          if (el) patch[id] = el.value;
        }
        onChange(patch);
      },
      hideToolbar: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countrySelected]);

  const cardClass = ['hi-ui-ssl-info__section', invalid && 'hi-ui-ssl-info__section--error']
    .filter(Boolean)
    .join(' ');

  const markTouched = (key: string) => setTouched((t) => ({ ...t, [key]: true }));

  const renderField = (spec: FieldSpec) => {
    const err = errorFor(spec);
    const show = (touched[spec.key] || showErrors) && err !== null;
    return (
      <FormField
        key={spec.key}
        label={spec.label}
        required={spec.required}
        error={show ? err : undefined}
        helperText={spec.helperText}
        fullWidth
      >
        <Input
          id={`ssl-${spec.key}`}
          name={spec.key}
          type={spec.type}
          value={value[spec.key] ?? ''}
          fullWidth
          onChange={(e) => onChange({ [spec.key]: e.target.value } as Partial<OrgFormData>)}
          onBlur={() => markTouched(spec.key)}
        />
      </FormField>
    );
  };

  // Country gates the address entry: personal/org fields, then Country, then the
  // address fields (revealed only once a country is chosen).
  const nonAddressSpecs = specs.filter((s) => !s.addressField);
  const addressSpecs = specs.filter((s) => s.addressField);

  return (
    <Card
      className={cardClass}
      variant="surface"
      header={<Text variant="heading-3">Organisation details</Text>}
    >
      <div className="hi-ui-ssl-info__section-body">
        <Text variant="body">
          Fill out the form below with your details. Please note that these details will be used to
          verify your certificate and our issuer will contact you via email and/or phone if
          required.
        </Text>

        <div className="hi-ui-ssl-info__form-grid">
          {nonAddressSpecs.map(renderField)}

          <FormField
            label="Country"
            required
            helperText="Select your country to enter your address details."
            error={
              (touched.country || showErrors) && !countrySelected
                ? 'This field is required'
                : undefined
            }
            fullWidth
          >
            <Select
              id="ssl-country"
              name="country"
              value={value.country ?? ''}
              fullWidth
              onChange={(e) => {
                onChange({ country: e.target.value });
                markTouched('country');
              }}
            >
              <option value="">Select a country</option>
              {COUNTRY_LIST.map((c, i) => (
                <option key={`${c.value}-${i}`} value={c.value}>
                  {c.name}
                </option>
              ))}
            </Select>
          </FormField>

          {countrySelected && (
            <>
              <FormField label="Find your address" fullWidth>
                <Input
                  id="ssl-ideal"
                  name="ideal"
                  type="text"
                  placeholder="Start typing your address"
                  fullWidth
                />
              </FormField>
              {addressSpecs.map(renderField)}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};
